import { cloneDeep } from 'lodash';
import { DefaultNodeModel, DefaultPortModel } from 'storm-react-diagrams';
import { Constant } from '../const';
import {
  ConfigView,
  ConfigViewType,
  DesignErrorKey,
  DesignInput,
  DesignItemType,
  DesignModuleNode,
  DesignModuleNodeConfig,
  DesignModuleNodeProps,
  DesignOutput,
  DesignPermission,
  DesignPermissionGroup,
  DesignPermissions,
  IconClass,
  ItemListConfigViewView,
  NodeTypeName,
  PortSide
} from '../types';
import Utility from '../utilities/Utility';
import { default as BaseLinkModel } from './BaseLinkModel';
import { default as BasePortModel } from './BasePortModel';
import { normalizeObjectOrNode, updateLastChange } from '../utilities';

type PartialNodeProps = Partial<DesignModuleNodeProps> & {
  config: Partial<DesignModuleNodeConfig>;
};

export default class BaseNodeModel extends DefaultNodeModel {
  props: DesignModuleNodeProps;
  x: number;
  y: number;
  ports: Record<string, DefaultPortModel & BasePortModel>;
  selectEvent: CustomEvent;
  writeProxy?: (key: string, value: unknown) => void;

  type = DesignItemType.object;
  DEFAULT_COLOR_CLASS = 'primary';
  isIgnored = false;

  constructor(
    nodeName = 'UNTITLE_NODE',
    x = 0,
    y = 0,
    props: PartialNodeProps | null = null,
    id: string | null = null
  ) {
    super(nodeName, 'var(--color-background-lightest)');
    if (id) {
      this.id = id;
    }
    const timestamp = window.apm?.timestamp ?? Date.now();
    this.props = {
      createdTime: timestamp,
      lastLocalChange: timestamp,
      lastSavedChange: 0,
      nodeTypeName: NodeTypeName.blankObject,
      colorClass: 'primary',
      iconClass: IconClass.disc,
      error: {},
      config: {
        inputs: [],
        outputs: [],
        disabledInFlow: false
      },
      configView: [],
      permission: {},
      flowVersion: 1
    };
    this.x = x;
    this.y = y;
    this.ports = {};
    this.initPorts();
    this.applyProps(props);
    this.selectEvent = new CustomEvent('ev-node-select', { detail: this.id });
  }

  /**
   * Get all links related to this node
   */
  getLinks(): BaseLinkModel[] {
    const links: Record<string, BaseLinkModel> = {};
    Object.values(this.ports).forEach((port) => {
      Object.values(port.links).forEach((link) => {
        links[link.id] = link as BaseLinkModel;
      });
    });
    return Object.values(links);
  }

  /**
   * Get all links going out of this node
   */
  getOutLinks(): BaseLinkModel[] {
    return this.getLinks().filter((link) => {
      return link.sourcePort?.parentNode.id === this.id;
    });
  }

  /**
   * Get all links going into this node
   */
  getInLinks(): BaseLinkModel[] {
    return this.getLinks().filter((link) => {
      return link.targetPort?.parentNode.id === this.id;
    });
  }

  /** Override this.props.nodeTypeName displayed at the top of the property panel. */
  nodeTypeName(): string | null {
    return null;
  }

  applyProps(props: PartialNodeProps | null): void {
    if (!props) {
      return;
    } else if (props.config && !('disabledInFlow' in props.config)) {
      props.config.disabledInFlow = false;
    }
    Object.assign(this.props, props);
  }

  /**
   * Set a write proxy function, which will be called upon setConfig
   */
  setWriteProxy(proxyFunc: (key: string, value: unknown) => void): void {
    this.writeProxy = proxyFunc;
  }

  /**
   * Does the node has a write proxy?
   */
  hasWriteProxy(): boolean {
    return typeof this.writeProxy === 'function';
  }

  /**
   * Set model property
   */
  setProperty<K extends keyof(DesignModuleNodeProps)>(
    key: K,
    value: DesignModuleNodeProps[K],
    ref: boolean
  ): void {
    const valueClone = cloneDeep(value);
    if (!ref && (key === 'colorClass' || key === 'iconClass')) {
      window.apm.updateParentNode(this, this.props.parentComponentID, key, value, this.type);
    }
    this.props[key] = valueClone;
    if (this.hasWriteProxy()) {
      this.writeProxy!(key, valueClone);
    }
  }

  /**
   * Get model property
   */
  getProperty<K extends keyof(DesignModuleNodeProps)>(
    key: K
  ): DesignModuleNodeProps[K] {
    return this.props[key];
  }

  /**
   * Set model config, which corresponds to properties shown by the property panel
   */
  setConfig<K extends keyof(DesignModuleNodeConfig)>(
    key: K,
    value: DesignModuleNodeConfig[K]
  ): void;
  setConfig(key: 'name', value: DesignModuleNode['name']): void;
  setConfig<K extends keyof(DesignModuleNodeConfig)>(
    key: K | 'name',
    value: DesignModuleNodeConfig[K] | DesignModuleNode['name']
  ): void {
    const valueClone = cloneDeep(value);
    switch (key) {
      case 'name':
        this.name = valueClone as DesignModuleNode['name'];
        break;
      default:
        this.props.config[key] = valueClone as DesignModuleNodeConfig[K];
        if (
          key === 'disabledInFlow'
          && this.id === this.props.parentComponentID
        ) {
          window.apm.postEventToCurrentView({ target: 'request-disable-module-from-flow', module: this, value });
        }
        break;
    }
    updateLastChange(this.props);
    if (this.hasWriteProxy()) {
      this.writeProxy!(key, valueClone);
    }
    normalizeObjectOrNode(this);
  }

  /** Set config value on this model, all {DesignModuleNode}s, and @see DesignObject. */
  setConfigAndParent<K extends keyof(DesignModuleNodeConfig)>(
    key: K,
    newValue: DesignModuleNodeConfig[K]
  ): void;
  setConfigAndParent(key: 'name', newValue: DesignModuleNode['name']): void;
  setConfigAndParent<K extends keyof(DesignModuleNodeConfig)>(
    key: K | 'name',
    newValue: DesignModuleNodeConfig[K] | DesignModuleNode['name']
  ): void {
    const oldValue = (key === 'name') ? this.name : this.props.config[key];
    this.setConfig(key as K, newValue as DesignModuleNodeConfig[K]);
    window.apm.updateParentNode(
      this,
      this.props.parentComponentID,
      key,
      newValue,
      this.type,
      oldValue
    );
  }

  /**
   * Get model config
   */
  getConfig<K extends keyof(DesignModuleNodeConfig)>(
    key: K
  ): DesignModuleNodeConfig[K];
  getConfig(key: 'name'): DesignModuleNode['name'];
  getConfig<K extends keyof(DesignModuleNodeConfig)>(
    key: K | 'name'
  ): DesignModuleNodeConfig[K] | DesignModuleNode['name'] {
    return (key === 'name') ? this.name : this.props.config[key];
  }

  saveChanges(key: string, value: any): void {
    const nodeId = this.hasWriteProxy() ? this.id : this.props.parentComponentID;
    window.apm.updateParentNode(this, nodeId, key, value, this.type);
    document.dispatchEvent(new CustomEvent('ev-property-refresh'));
  }

  getDefaultColorClass(): string {
    return this.DEFAULT_COLOR_CLASS;
  }

  /**
   * Update two-way reference of variables
   */
  updateVariables(
    oldInputs: DesignInput[] | null = null,
    oldOutputs: DesignOutput[] | null = null
  ): void {
    if (oldInputs || oldOutputs) {
      this.removeVariableRefs(oldInputs, oldOutputs);
    }
    const modId = this.hasWriteProxy() ? null : window.apm.getCurrentDiagramWidget().props.node.id;
    const usedId = this.hasWriteProxy() ? this.id : this.props.parentComponentID;
    if (this.getConfig('inputs')) {
      this.setVariables(this.getConfig('inputs')!, modId, usedId, 'set');
    }
    if (this.getConfig('outputs')) {
      this.setVariables(this.getConfig('outputs')!, modId, usedId, 'set');
    }
  }

  /**
   * Remove variable references
   */
  removeVariableRefs(
    oldInputs: DesignInput[] | null = null,
    oldOutputs: DesignOutput[] | null = null
  ): void {
    let inputs = oldInputs;
    let outputs = oldOutputs;
    if (this.getConfig('inputs') && oldInputs === null) {
      inputs = this.getConfig('inputs')!;
    }
    if (this.getConfig('outputs') && oldOutputs === null) {
      outputs = this.getConfig('outputs')!;
    }
    const modId = this.hasWriteProxy() ? null : window.apm.getCurrentDiagramWidget().props.node.id;
    const usedId = this.hasWriteProxy() ? this.id : this.props.parentComponentID;
    if (inputs) {
      this.setVariables(inputs, modId, usedId, 'del');
    }
    if (outputs) {
      this.setVariables(outputs, modId, usedId, 'del');
    }
  }

  setVariables(
    variables: DesignInput[] | DesignOutput[],
    _: any,
    usedId: string | undefined,
    operation: 'del' | 'set'
  ): void {
    variables.forEach((variable) => {
      if (!variable.varName?.includes('.')) {
        return;
      }
      const [scope, varName] = variable.varName.split('.');
      if (scope === Constant.k.SCOPE_APPLICATION) {
        if (operation === 'set') {
          window.apm.setVariable(varName, variable.type, usedId);
        }
        if (operation === 'del') {
          window.apm.removeVariable(varName, usedId);
        }
      }
      if (scope === Constant.k.PARAMETERS && variables[0].value) {
        this.props.config['inputs'] = variables as DesignInput[];
      }
    });
  }

  dfs(
    visited: { nodes: Record<string, boolean>; },
    options: {
      staticInputs?: {
        value: string;
        varName: string;
      }[];
      type: 'configEvaluation';
    }
  ): void {
    // Skip visited
    if (visited.nodes[this.getID()]) {
      return;
    }
    visited.nodes[this.getID()] = true;
    const outs = this.getOutLinks();
    const { staticInputs = [], type } = options;
    outs.forEach((out) => {
      if (type === 'configEvaluation') {
        if (this.isIgnored) {
          // Since this node can't be reached, its outgoing edges can't be reached
          out.setIgnored(true);
        } else {
          if (staticInputs.length) {
            // Reachability by condition true/false
            let cond = out.serializeCondition(out.getConfig('condition'));
            staticInputs.forEach((input) => {
              cond = cond.replace(input.varName, Utility.getTypedValueForStatement(input.value));
            });
            const scopeReg = new RegExp(`(${Constant.k.SCOPE_APPLICATION}|${Constant.k.SCOPE_MODULE})\\.`, 'ig');
            out.setIgnored(
              // Cannot evaluate since there are non-static references
              !cond.match(scopeReg)
              // If all references have been replaced
              && !Utility.evaluateStatement(cond)
            );
          } else {
            // Reachable since there aren't any config values to use for this evaluation
            out.setIgnored(false);
          }
        }
      }
      out.dfs(visited, options);
    });
  }

  setIgnored(isIgnored = false): void {
    this.isIgnored = isIgnored;
    document.dispatchEvent(new CustomEvent('ev-graph-node-ignore', { detail: this.getID() }));
  }

  setPermission(group: DesignPermissionGroup, value: DesignPermission): void {
    const valueClone = cloneDeep(value);
    this.props.permission ??= {};
    this.props.permission[group] = valueClone;
    if (this.hasWriteProxy()) {
      this.writeProxy!(group, valueClone);
    }
  }

  getPermission(group: DesignPermissionGroup): DesignPermission {
    return this.getPermissions()[group];
  }

  getPermissions(): Required<DesignPermissions> {
    const permissions: DesignPermissions = {};
    (Object.entries(Constant.k.USER_GROUP) as (
      [DesignPermissionGroup, { defaultValue: DesignPermission; }][]
    )).forEach(([group, { defaultValue }]) => {
      const customValue = this.props.permission?.[group];
      permissions[group] = cloneDeep(customValue ?? defaultValue);
    });
    return permissions as Required<DesignPermissions>;
  }

  /**
   * Get view configurations for @see PropertyPanelWidget.
   */
  getConfigViews(): ConfigView[] {
    return [
      ...this.getConfigViewNameField(),
      {
        type: ConfigViewType.input,
        label: this.getPropLabel('description'),
        key: 'description'
      },
      {
        type: ConfigViewType.itemList,
        label: this.getPropLabel('inputs'),
        key: 'inputs',
        view: ItemListConfigViewView.argument
      },
      {
        type: ConfigViewType.itemList,
        label: this.getPropLabel('outputs'),
        key: 'outputs',
        view: ItemListConfigViewView.return
      },
      ...(this.props.configView ?? []),
      { type: ConfigViewType.hr },
      { type: ConfigViewType.textarea, label: this.getPropLabel('note'), key: 'note' }
    ];
  }

  /**
   * Get the "Name" input box
   * The value validation rules are different for component nodes, which has a write proxy, and diagram nodes which doesn't
   */
  getConfigViewNameField(isNameLocked = false): ConfigView[] {
    return [{
      type: ConfigViewType.input,
      label: this.getPropLabel('name'),
      key: 'name',
      valRules: {
        noDuplicateLib: this.hasWriteProxy(),
        noEmpty: true,
        objectName: true
      },
      disabled: isNameLocked
    }];
  }

  setError(key: DesignErrorKey, message: string): void {
    this.props.error ??= {};
    this.props.error[key] = message;
  }

  removeError(key: DesignErrorKey): void {
    if (this.props.error) {
      delete this.props.error[key];
    }
  }

  removeErrors(prefix: string): void {
    const { error = {} } = this.props;
    Object.keys(error).forEach((key) => {
      if (key.startsWith(prefix)) {
        delete error![key as DesignErrorKey];
      }
    });
  }

  hasError(): boolean {
    return Object.keys(this.props.error ?? {}).length > 0;
  }

  /**
   * Get the last error message
   */
  getError(): string {
    const e = Object.values(this.props.error ?? {});
    if (e.length) {
      return e[e.length - 1];
    }
    return '';
  }

  /**
   * By default general node is not special; canvas should use general renderer
   */
  isSpecialNode(): boolean {
    return false;
  }

  /**
   * Set that the model's view is selected by user (or not)
   */
  setSelected(selected: boolean): void {
    const currentSel = this.selected;
    super.setSelected(selected);
    if (currentSel !== selected) {
      document.dispatchEvent(this.selectEvent);
    }
  }

  /**
   * Initialize node ports
   */
  initPorts(): void {
    const portsUnitVectors: Record<PortSide, [number, number]> = {
      [PortSide.left]: [-1, 0],
      [PortSide.right]: [1, 0],
      [PortSide.top]: [0, -1],
      [PortSide.bottom]: [0, 1]
    };
    Object.entries(portsUnitVectors).forEach(([key, [x, y]]) => {
      const port = new BasePortModel(key as PortSide);
      port.unitVector = { x, y };
      this.addPort(port);
    });
  }

  getPorts(): Record<string, BasePortModel> {
    return this.ports;
  }

  remove(): void {
    this.removeVariableRefs();
    super.remove();
  }

  getPropLabel(key: keyof typeof window.l.property): string {
    return window.l.property[key];
  }

  getOutputSuggestions(): {
    out: string[];
    value: string[];
  } {
    // "out" suggests possible variables output by the node (VariableDataModel)
    // "value" suggests possible values of the node's output
    return {
      out: [],
      value: []
    };
  }

  /**
   * Serialize model for storage
   */
  serialize() {
    const serialized = super.serialize() as any;
    delete serialized.extras;
    delete serialized.selected;
    delete serialized.ports;
    delete serialized.color;
    serialized.name = this.name;
    serialized.type = this.type;
    serialized.props = cloneDeep(this.props);
    delete serialized.props.configView;
    return serialized;
  }
}
